import { supabase } from "@/shared/lib/supabase";
import type { Json, VariableKind } from "@/types/supabase.types";
import type {
  InputFieldDefinition,
  VariableInput,
  VariableValueInput,
} from "@/domains/templates/types";

export type TemplateStepPreset = {
  id: string;
  name: string;
  order: number;
  inputFields: InputFieldDefinition[];
  scopedInputFields?: InputFieldDefinition[];
};

export type SourceTemplatePreset = {
  hasSubanalysisInvocation?: boolean;
  id?: string | null;
  name?: string | null;
  type?: string | null;
  context?: string | null;
  inputFields: InputFieldDefinition[];
  scopedInputFields?: InputFieldDefinition[];
  steps: TemplateStepPreset[];
};

export type CreateAnalysisFromTemplateInput = {
  templateId: string;
  name: string;
  description?: string | null;
  context?: string | null;
  collectionId: string;
  displayInputResults?: Record<string, string>;
  stepDisplayInputResults?: Record<string, Record<string, string>>;
  variableValues?: VariableValueInput[];
  stepVariableValues?: Record<string, VariableValueInput[]>;
};

function resolveVariableKind(
  variableGroup: string | null | undefined,
  variableKind: VariableKind | undefined,
): VariableKind {
  if (variableGroup === "tools") return "tool";
  if (variableGroup === "references") return "context";
  if (variableGroup === "search" || variableGroup === "patent") {
    return "input";
  }
  return variableKind ?? "input";
}

export function inputFieldDefinitionsToDisplayInputVariables(
  definitions: InputFieldDefinition[],
): Json {
  return definitions.reduce<Record<string, Json>>((fields, definition) => {
    const displayName =
      definition.variableNameFrontend?.trim() || definition.key.trim();
    if (!displayName) return fields;

    const description = definition.description?.trim() || undefined;
    fields[displayName] = {
      type: definition.type,
      ...(description ? { description } : {}),
      ...(definition.source ? { source: definition.source } : {}),
      ...(definition.variableId ? { variable_id: definition.variableId } : {}),
      ...(definition.sourceVariableId
        ? { source_variable_id: definition.sourceVariableId }
        : {}),
      ...(definition.variableNameFrontend !== undefined
        ? { variable_name_frontend: definition.variableNameFrontend }
        : {}),
      ...(definition.variableGroup !== undefined
        ? { variable_group: definition.variableGroup }
        : {}),
      ...(definition.variableKind !== undefined
        ? { variable_kind: definition.variableKind }
        : {}),
      ...(definition.variableOptions !== undefined
        ? { variable_options: definition.variableOptions }
        : {}),
      ...(definition.variableLinkedSchema !== undefined
        ? { variable_linked_schema: definition.variableLinkedSchema }
        : {}),
      ...(definition.variableLinkedTable !== undefined
        ? { variable_linked_table: definition.variableLinkedTable }
        : {}),
      ...(definition.variableLinkedColumnId !== undefined
        ? { variable_linked_column_id: definition.variableLinkedColumnId }
        : {}),
      ...(definition.variableLinkedColumnName !== undefined
        ? { variable_linked_column_name: definition.variableLinkedColumnName }
        : {}),
      ...(definition.defaultValue !== undefined
        ? { default_value: definition.defaultValue }
        : {}),
      ...(definition.required !== undefined
        ? { required: definition.required }
        : {}),
      ...(definition.visible !== undefined
        ? { visible: definition.visible }
        : {}),
      ...(definition.order !== undefined ? { order: definition.order } : {}),
      ...(definition.tier ? { variable_tier: definition.tier } : {}),
      ...(definition.parentVariableId !== undefined
        ? { parent_variable_id: definition.parentVariableId }
        : {}),
    };
    return fields;
  }, {});
}

export function inputFieldDefinitionsToVariables(
  definitions: InputFieldDefinition[],
): VariableInput[] {
  return definitions.flatMap((definition, index) => {
    const displayName =
      definition.variableNameFrontend?.trim() || definition.key.trim();
    if (!displayName) return [];

    return [
      {
        ...(definition.variableId
          ? { variable_id: definition.variableId }
          : {}),
        ...(definition.source === "variable" && definition.sourceVariableId
          ? { source_variable_id: definition.sourceVariableId }
          : {}),
        ...((definition.variableId || definition.source === "variable") &&
        definition.key.trim()
          ? { variable_name: definition.key.trim() }
          : {}),
        variable_name_frontend: displayName,
        variable_description: definition.description?.trim() || null,
        variable_group: definition.variableGroup ?? "custom",
        variable_kind: resolveVariableKind(
          definition.variableGroup,
          definition.variableKind,
        ),
        variable_type: definition.type,
        variable_options: definition.variableOptions ?? [],
        variable_linked_schema: definition.variableLinkedSchema ?? null,
        variable_linked_table: definition.variableLinkedTable ?? null,
        variable_linked_column_id: definition.variableLinkedColumnId ?? null,
        variable_linked_column_name:
          definition.variableLinkedColumnName ?? null,
        variable_default: definition.defaultValue ?? null,
        variable_is_required: definition.required ?? false,
        variable_is_visible: definition.visible ?? true,
        variable_order: definition.order ?? index,
        variable_tier: definition.tier ?? "standard",
        parent_variable_id: definition.parentVariableId ?? null,
      },
    ];
  });
}

export function inputFieldDefinitionsToVariableValues(
  definitions: InputFieldDefinition[],
): VariableValueInput[] {
  return definitions.flatMap((definition) => {
    if (
      definition.defaultValue === undefined ||
      definition.defaultValue === null ||
      definition.defaultValue === ""
    ) {
      return [];
    }

    const displayName =
      definition.variableNameFrontend?.trim() || definition.key.trim();
    if (!displayName) return [];

    return [
      {
        ...(definition.variableId
          ? { variable_id: definition.variableId }
          : definition.source === "variable" && definition.key.trim()
            ? { variable_name: definition.key.trim() }
            : { variable_name_frontend: displayName }),
        value: definition.defaultValue,
      },
    ];
  });
}

type FunctionErrorWithContext = {
  context?: unknown;
};

async function throwFunctionInvokeError(error: unknown): Promise<never> {
  const context =
    error && typeof error === "object" && "context" in error
      ? (error as FunctionErrorWithContext).context
      : undefined;

  if (context instanceof Response) {
    const payload: unknown = await context
      .clone()
      .json()
      .catch(() => error);
    throw payload;
  }

  throw context ?? error;
}

export async function createAnalysisFromTemplate({
  templateId,
  name,
  description,
  context,
  collectionId,
  displayInputResults,
  stepDisplayInputResults,
  variableValues,
  stepVariableValues,
}: CreateAnalysisFromTemplateInput): Promise<string> {
  const trimmedName = name.trim();
  if (!trimmedName) throw new Error("Analysis name is required");

  const trimmedDescription = description?.trim();
  const trimmedContext = context?.trim();

  const response = await supabase.functions.invoke(
    `template/${templateId}/createAnalysis`,
    {
      method: "POST",
      body: {
        name: trimmedName,
        description: trimmedDescription ?? null,
        tenant_collection_id: collectionId,
        user_context: trimmedContext ?? null,
        display_input_results: displayInputResults ?? {},
        step_display_input_results: stepDisplayInputResults ?? {},
        variable_values: variableValues ?? [],
        step_variable_values: stepVariableValues ?? {},
      },
    },
  );

  if (response.error) await throwFunctionInvokeError(response.error);

  const payload = response.data as unknown;
  if (
    !payload ||
    typeof payload !== "object" ||
    !("analysis_id" in payload) ||
    typeof (payload as { analysis_id?: unknown }).analysis_id !== "string"
  ) {
    throw new Error("Missing analysis_id from create analysis from template");
  }

  return (payload as { analysis_id: string }).analysis_id;
}
